import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, DialogContent, Grid, IconButton, Stack, Switch, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  WorkspaceLogoDTO,
  getAllLogos,
  updateLogoVisibleById,
  updateStatusLogoById,
} from 'wisestep-api';
import { getDateUsingFormat, WsButton, WsColors, WsDataTable, WsDialog } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { getFile } from '../../../features/careerPageSettings/util';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { workspaceSettingsIndexOptions } from '../helper/workspaceSettingsHelper';

import { logoPreviewContainerStyle } from './OrganizationLogoUpload';

const LogoListing = () => {
  const navigate = useNavigate();
  const [logoList, setLogoList] = useState<WorkspaceLogoDTO[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const setNotification = useNotificationStore((state) => state.setNotification);

  const openUploadLogo = () => {
    navigate('/workspace-settings/organization-logo/upload');
  };

  const openPreviewLogo = async (s3Url: string) => {
    const fileUrl = await getFile(s3Url);
    if (fileUrl) {
      setImageUrl(fileUrl);
      setOpenDialog(true);
    }
  };

  const closePreviewLogo = () => {
    setOpenDialog(false);
    setImageUrl('');
  };

  const updateLogoStatus = async (id: string, status: boolean) => {
    const hasMultipleDefaultStatus =
      Array.isArray(logoList) && logoList?.filter((item) => item.workspaceDefault).length == 1;
    if (status && hasMultipleDefaultStatus) {
      setNotification(failed('Cannot set more than one default status.'));
    } else {
      const response = await updateStatusLogoById(id, status);
      if (response) {
        fetchAll();
      }
    }
  };

  const updateLogoVisibilty = async (id: string, visible: boolean) => {
    const response = await updateLogoVisibleById(id, visible);
    if (response) {
      fetchAll();
    }
  };

  const fetchAll = async () => {
    const response = await getAllLogos();
    setLogoList(response.data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const columns = [
    {
      Header: 'Organization Entity',
      accessor: 'orgEntityName',
    },
    {
      Header: 'Logo',
      accessor: 's3Url',
      Cell: (cell: Cell) => {
        return (
          <IconButton onClick={() => openPreviewLogo(logoList[cell.row.index].s3Url.split('?')[0])}>
            <ImageIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
          </IconButton>
        );
      },
    },
    {
      Header: 'Is Default Logo',
      accessor: 'workspaceDefault',
      Cell: (cell: Cell) => {
        return (
          <Switch
            checked={logoList[cell.row.index].workspaceDefault}
            onChange={() =>
              updateLogoStatus(
                logoList[cell.row.index].id ?? '',
                !logoList[cell.row.index].workspaceDefault
              )
            }
            color="primary"
          />
        );
      },
    },
    {
      Header: 'Created By',
      accessor: 'createdByName',
    },
    {
      Header: 'Created On',
      accessor: 'createdOn',
    },
    {
      Header: 'Status',
      accessor: 'actions',
      Cell: (cell: Cell) => {
        return !logoList[cell.row.index].markActive ? (
          <IconButton onClick={() => updateLogoVisibilty(logoList[cell.row.index].id ?? '', true)}>
            <Tooltip title={'Show/Hide'}>
              <VisibilityOffIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
            </Tooltip>
          </IconButton>
        ) : (
          <IconButton onClick={() => updateLogoVisibilty(logoList[cell.row.index].id ?? '', false)}>
            <Tooltip title={'Show/Hide'}>
              <VisibilityIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
            </Tooltip>
          </IconButton>
        );
      },
    },
  ];

  const data = useMemo(
    () =>
      logoList?.map((logo) => ({
        id: logo?.id ?? '',
        orgEntityName: logo.orgEntityName ?? 'Not available',
        s3Url: logo?.s3Url,
        workspaceDefault: logo?.workspaceDefault,
        createdByName: logo.createdByName,
        createdOn: logo?.createdOn
          ? getDateUsingFormat(logo.createdOn?.toString(), 'DD-MMM-YY')
          : 'Not Available',
      })),
    [logoList]
  );

  return (
    <>
      <SettingsPageLayout
        title="Uploaded Logos"
        indexOptions={workspaceSettingsIndexOptions}
        breadCrumnbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.CRM,
          Pages['UPLOADED_LOGOS']
        )}
      >
        <Stack direction="row" p={2} justifyContent="flex-end" alignItems="center">
          <WsButton startIcon={<AddIcon />} variant="outlined" onClick={() => openUploadLogo()}>
            Upload logo
          </WsButton>
        </Stack>
        <WsDataTable dense columns={columns} data={data ?? []} />
      </SettingsPageLayout>
      <PreviewModal openDialog={openDialog} imageUrl={imageUrl} handleClose={closePreviewLogo} />
    </>
  );
};

const PreviewModal = ({ openDialog, imageUrl, handleClose }) => (
  <WsDialog openDialog={openDialog} size="medium" onClose={handleClose}>
    <DialogContent>
      <Grid container py={1} ml={1} mr={1}>
        <Grid item p={1} sm={12} mr={2} justifyContent="center" alignContent="center">
          <Box
            component="img"
            sx={logoPreviewContainerStyle}
            src={imageUrl}
            justifyContent="center"
            alignItems="center"
          />
        </Grid>
      </Grid>
    </DialogContent>
  </WsDialog>
);

export default LogoListing;
